import type { Observer } from 'rxjs';
import { Observable } from 'rxjs';
import { useRef } from 'react';
import queryString from 'query-string';

import {
  isRequestOk,
  getResponseHeaders,
  formatBody,
  setHeaders,
  areRequestParamsEquals
} from './utils';
import requestCache from './cache';
import type { RequestResponse, RequestProgress, RequestParams, RequestOptions } from './types';

function request<D>(
  params: RequestParams,
  options: RequestOptions = {},
  queryStringsOptions: queryString.StringifyOptions | undefined = undefined
): Observable<RequestResponse<D>> {
  return new Observable((observer: Observer<RequestResponse<D>>) => {
    const { url, method = 'GET', headers, query, body, responseType = 'json' } = params;
    const { abortOnUnsubscribe = true, withProgress = false, useCache = false } = options;

    const cachedResponse = useCache ? requestCache.getCachedResponse(params) : undefined;

    if (cachedResponse) {
      // having a sync .next() create unexpecter behavior in `useObservable` hook
      // the onComplete is called before data dispatch
      // so the onComplete callback was called with indefined (because data wasn't dispatch)
      // make .next() async solve this weird behavior
      setTimeout(() => {
        observer.next(cachedResponse);
        observer.complete();
      }, 0);
    } else {
      // if we don't use cache or if we don't find a cached response
      // we send a new request
      let progress: RequestProgress = { loaded: 0, total: 1 };
      const xhr = new XMLHttpRequest();

      const urlToCall = query ? `${url}?${queryString.stringify(query, queryStringsOptions)}` : url;
      xhr.responseType = responseType;

      xhr.onload = () => {
        if (isRequestOk(xhr)) {
          const requestResponse: RequestResponse<D> = {
            data: xhr.response,
            progress: progress.loaded === 0 ? { loaded: 1, total: 1 } : progress,
            statusCode: xhr.status,
            headers: getResponseHeaders(xhr)
          };

          if (useCache) {
            requestCache.addResponse(requestResponse, params);
          }
          observer.next(requestResponse);
          observer.complete();
        } else {
          observer.error({
            message: xhr.response,
            statusCode: xhr.status,
            headers: getResponseHeaders(xhr)
          });
        }
      };

      xhr.onerror = () => {
        observer.error({
          message: xhr.statusText,
          statusCode: xhr.status
        });
      };

      if (withProgress) {
        observer.next({ progress: { loaded: 0, total: 1 }, statusCode: xhr.status });
        xhr.onprogress = ({ loaded, total }: ProgressEvent) => {
          progress = { loaded, total };
          observer.next({ progress, statusCode: xhr.status });
        };
      }

      xhr.open(method, urlToCall);
      setHeaders(headers, xhr);

      xhr.send(formatBody(body));

      return () => {
        if (abortOnUnsubscribe && !xhr.response) {
          xhr.abort();
        }
      };
    }
  });
}

function useRequestObservableMemo<D>(
  params: RequestParams,
  options: RequestOptions = {}
): Observable<RequestResponse<D>> {
  const prevParams = useRef<RequestParams | null>(null);
  const requestRef = useRef(request<D>(params, options));

  if (prevParams.current) {
    if (!areRequestParamsEquals(prevParams.current, params)) {
      requestRef.current = request<D>(params, options);
    }
  }

  return requestRef.current;
}

export { request, useRequestObservableMemo, areRequestParamsEquals };
export type { RequestParams, RequestResponse };
